import {
  expectedMix,
  questionsLookNearDuplicate,
  REQUIRED_EXERCISE_COUNT,
  validateExercise,
} from "../../../domain/exercises/validate.ts";
import {
  meaningPackageSchema,
  type ComparisonCandidate,
  type MeaningPackage,
  type PackageOutcome,
  type SelectedSense,
} from "../../../contracts/ai/schemas.ts";
import { isProductError } from "../../../contracts/errors.ts";
import type { Exercise } from "../../../domain/vocabulary/types.ts";
import {
  groupedExercisesLooseSchema,
  type ConfusableDrill,
  type GroupedExercises,
  type PackageCard,
  type SimpleDrill,
} from "./generationSchemas.ts";

export const MAX_PACKAGE_REPAIR_ROUNDS = 2;

export function looksLikePlaceholderExercises(value: unknown): boolean {
  if (!Array.isArray(value) || value.length === 0) {
    return true;
  }
  return value.every(
    (item) => item === null || typeof item === "number" || typeof item === "boolean" || typeof item === "string",
  );
}

function firstTopLevelHint(value: string): string {
  let depth = 0;
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === "(" || char === "[" || char === "{") {
      depth += 1;
      continue;
    }
    if (char === ")" || char === "]" || char === "}") {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth > 0) {
      continue;
    }
    if (char === "," || char === ";" || char === "/") {
      return value.slice(0, index).trim();
    }
  }
  return value.trim();
}

export function sanitizeRussianHint(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const clipped = firstTopLevelHint(trimmed).slice(0, 80).trim();
  return clipped || null;
}

export function uniqueStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase().replace(/\s+/g, " ");
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

export function filterRelations(
  relations: PackageCard["relations"],
  comparisonIndex: readonly ComparisonCandidate[],
  selectedItemId: string,
): PackageCard["relations"] {
  const allowed = new Set(comparisonIndex.map((item) => item.meaningId));
  const seen = new Set<string>();
  return relations.filter((relation) => {
    if (!relation.targetMeaningId || relation.targetMeaningId === selectedItemId) {
      return false;
    }
    if (!allowed.has(relation.targetMeaningId) || seen.has(relation.targetMeaningId)) {
      return false;
    }
    seen.add(relation.targetMeaningId);
    return true;
  });
}

function mapSimpleDrills(
  kind: "context_recall" | "meaning_to_word",
  drills: readonly SimpleDrill[],
  itemId: string,
  prefix: string,
  limit: number,
): Exercise[] {
  return drills.slice(0, limit).map((drill, index) => {
    const correctAnswer = drill.correctAnswer.trim();
    return {
      id: `${itemId}-${prefix}-${index}`,
      kind,
      question: drill.question.trim(),
      correctAnswer,
      acceptedAnswers: uniqueStrings([correctAnswer, ...(drill.acceptedAnswers ?? [])]),
      explanation: drill.explanation.trim(),
      referencedMeaningIds: [],
      relatedWordDistinctions: [],
    };
  });
}

function mapConfusableDrills(
  drills: readonly ConfusableDrill[],
  itemId: string,
  relations: PackageCard["relations"],
  limit: number,
): Exercise[] {
  return drills.slice(0, limit).map((drill, index) => {
    const correctAnswer = drill.correctAnswer.trim();
    const labels = uniqueStrings([correctAnswer, ...drill.options]);
    const options = labels.slice(0, 4).map((label, optionIndex) => ({
      id: `${itemId}-cf-${index}-opt-${optionIndex}`,
      label,
    }));
    const correctOption =
      options.find((option) => option.label.trim().toLowerCase() === correctAnswer.toLowerCase()) ?? options[0];
    const relation = relations[index % Math.max(relations.length, 1)];
    const related = relation
      ? [{ meaningId: relation.targetMeaningId, explanation: relation.explanation }]
      : [];
    return {
      id: `${itemId}-cf-${index}`,
      kind: "confusable_context" as const,
      question: drill.question.trim(),
      correctAnswer,
      acceptedAnswers: uniqueStrings([correctAnswer, ...(drill.acceptedAnswers ?? [])]),
      explanation: drill.explanation.trim(),
      referencedMeaningIds: related.map((item) => item.meaningId),
      relatedWordDistinctions: related,
      options,
      correctOptionId: correctOption?.id ?? options[0]?.id ?? `${itemId}-cf-${index}-opt-0`,
    };
  });
}

export function parseGroupedExercises(raw: unknown): { ok: true; drills: GroupedExercises } | { ok: false; issues: string[] } {
  if (raw && typeof raw === "object" && "exercises" in raw && looksLikePlaceholderExercises(raw.exercises)) {
    return {
      ok: false,
      issues: ["Practice questions came back as placeholders instead of question objects."],
    };
  }
  const parsed = groupedExercisesLooseSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      issues: ["Practice questions were missing or not objects with question text and answers."],
    };
  }
  return { ok: true, drills: parsed.data };
}

export function collectExerciseIssues(exercises: readonly Exercise[], hasUsefulRelations: boolean): string[] {
  const issues: string[] = [];
  if (exercises.length !== REQUIRED_EXERCISE_COUNT) {
    issues.push(`Expected ${REQUIRED_EXERCISE_COUNT} exercises, received ${exercises.length}.`);
  }
  const mix = expectedMix(hasUsefulRelations);
  const counts = {
    context_recall: 0,
    meaning_to_word: 0,
    confusable_context: 0,
  };
  for (const exercise of exercises) {
    counts[exercise.kind] += 1;
    issues.push(...validateExercise(exercise).map((issue) => issue.message));
  }
  for (const kind of Object.keys(mix) as Array<keyof typeof mix>) {
    if (counts[kind] !== mix[kind]) {
      issues.push(`Expected ${mix[kind]} ${kind} exercises, received ${counts[kind]}.`);
    }
  }
  for (let i = 0; i < exercises.length; i += 1) {
    for (let j = i + 1; j < exercises.length; j += 1) {
      if (questionsLookNearDuplicate(exercises[i].question, exercises[j].question)) {
        issues.push(`Questions ${exercises[i].id} and ${exercises[j].id} are too similar.`);
      }
    }
  }
  return [...new Set(issues)];
}

export function assembleMeaningPackage(input: {
  sense: SelectedSense;
  card: PackageCard;
  drills: GroupedExercises;
  relations: PackageCard["relations"];
}): { ok: true; package: MeaningPackage } | { ok: false; issues: string[] } {
  const mix = expectedMix(input.relations.length > 0);
  const exercises = [
    ...mapSimpleDrills("context_recall", input.drills.contextRecall, input.sense.itemId, "cr", mix.context_recall),
    ...mapSimpleDrills("meaning_to_word", input.drills.meaningToWord, input.sense.itemId, "mw", mix.meaning_to_word),
    ...mapConfusableDrills(input.drills.confusableContext, input.sense.itemId, input.relations, mix.confusable_context),
  ];
  const issues = collectExerciseIssues(exercises, input.relations.length > 0);
  if (issues.length) {
    return { ok: false, issues };
  }
  const parsed = meaningPackageSchema.safeParse({
    itemId: input.sense.itemId,
    type: input.sense.type,
    shortLabel: input.card.shortLabel.trim() || input.sense.shortLabel,
    definition: input.card.definition.trim() || input.sense.definition,
    russianHint: sanitizeRussianHint(input.card.russianHint),
    example: input.card.example.trim() || input.sense.example,
    memoryKey: input.card.memoryKey?.trim() ? input.card.memoryKey.trim().slice(0, 160) : null,
    collocations: uniqueStrings(input.card.collocations).slice(0, 3),
    relations: input.relations,
    exercises,
    reviewNote: input.card.reviewNote?.trim() || null,
  });
  if (!parsed.success) {
    return { ok: false, issues: ["The generated card fields were invalid."] };
  }
  return { ok: true, package: parsed.data };
}

export async function completeMeaningPackage(options: {
  sense: SelectedSense;
  comparisonIndex: readonly ComparisonCandidate[];
  card: PackageCard;
  fetchExercises: (input: {
    relations: PackageCard["relations"];
    issues: string[] | null;
  }) => Promise<unknown>;
}): Promise<PackageOutcome> {
  const relations = filterRelations(options.card.relations, options.comparisonIndex, options.sense.itemId);
  let issues: string[] | null = null;
  for (let round = 0; round <= MAX_PACKAGE_REPAIR_ROUNDS; round += 1) {
    let raw: unknown;
    try {
      raw = await options.fetchExercises({ relations, issues });
    } catch (error) {
      if (isProductError(error)) {
        if (error.code === "cancelled") {
          throw error;
        }
        if (
          error.code === "missing_key" ||
          error.code === "invalid_key" ||
          error.code === "model_unavailable" ||
          error.code === "rate_limited" ||
          error.code === "offline" ||
          error.code === "timeout"
        ) {
          return {
            itemId: options.sense.itemId,
            status: "failed",
            code: error.code,
            message: error.message,
          };
        }
      }
      issues = ["The model did not return usable practice questions."];
      continue;
    }
    const grouped = parseGroupedExercises(raw);
    if (!grouped.ok) {
      issues = grouped.issues;
      continue;
    }
    const assembled = assembleMeaningPackage({
      sense: options.sense,
      card: options.card,
      drills: grouped.drills,
      relations,
    });
    if (assembled.ok) {
      return { itemId: options.sense.itemId, status: "ready", package: assembled.package };
    }
    issues = assembled.issues;
  }
  return {
    itemId: options.sense.itemId,
    status: "failed",
    code: "invalid_response",
    message: issues?.[0] ?? "The model returned an invalid learning package.",
  };
}
